import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * N-3-8：時間計算（時、分、秒）——三上第八章。
 * 題型：
 *   difficulty 1：讀鐘面時刻（文字描述鐘面 → 選出正確時刻）
 *   difficulty 2：時間加法（幾時幾分 + 若干分鐘 = ？）
 *   difficulty 3：時間差（兩時刻之間經過幾分鐘）
 */
export function generateTimeClock(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  if (difficulty === 1) {
    // Read clock face
    const hour = randInt(rand, 1, 12);
    const minuteChoices = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
    const minute = minuteChoices[randInt(rand, 0, minuteChoices.length - 1)]!;
    const correctStr = `${hour}:${pad2(minute)}`;

    // Distractors: shift hour ±1 or minute by 5
    const distractors: string[] = [];
    const variants = [
      `${hour === 12 ? 1 : hour + 1}:${pad2(minute)}`,
      `${hour === 1 ? 12 : hour - 1}:${pad2(minute)}`,
      `${hour}:${pad2((minute + 5) % 60)}`,
      `${hour}:${pad2((minute + 55) % 60)}`,
    ];
    for (const v of variants) {
      if (v !== correctStr && distractors.length < 3) distractors.push(v);
    }
    while (distractors.length < 3) {
      distractors.push(`${randInt(rand, 1, 12)}:${pad2(minuteChoices[randInt(rand, 0, minuteChoices.length - 1)]!)}`);
    }

    const options = [correctStr, ...distractors.slice(0, 3)];
    const shuffled = [...options].sort((x, y) => mulberry32(seed + x.length * 7)() - mulberry32(seed + y.length * 7)());
    const correctIdx = shuffled.indexOf(correctStr);

    const ampm = hour < 12 ? '上午' : '下午';
    const minuteDesc = minute === 0 ? '整點' : `${minute} 分`;

    return {
      kind: 'mcq',
      prompt: `${ampm} ${hour} 時 ${minuteDesc}，鐘面顯示的時刻是？`,
      options: shuffled,
      correctAnswer: correctIdx,
      explanation: `${ampm} ${hour} 時 ${minuteDesc} = ${correctStr}。`,
      kp: 'math.time.elapsed',
    };
  } else if (difficulty === 2) {
    // Time addition: start time + minutes = end time
    const startHour = randInt(rand, 7, 16);
    const startMin = randInt(rand, 0, 11) * 5; // 0,5,10..55
    const addMin = randInt(rand, 1, 11) * 5; // 5,10..55

    const totalMin = startHour * 60 + startMin + addMin;
    const endHour = Math.floor(totalMin / 60) % 24;
    const endMin = totalMin % 60;

    const correctStr = `${endHour}:${pad2(endMin)}`;
    const distractors: string[] = [
      `${endHour}:${pad2((endMin + 5) % 60)}`,
      `${(endHour + 1) % 24}:${pad2(endMin)}`,
      `${endHour}:${pad2((endMin + 55) % 60)}`,
    ];
    const options = [correctStr, ...distractors.slice(0, 3)];
    const shuffled = [...options].sort((x, y) => mulberry32(seed + x.length * 11)() - mulberry32(seed + y.length * 11)());
    const correctIdx = shuffled.indexOf(correctStr);

    return {
      kind: 'mcq',
      prompt: `${startHour}:${pad2(startMin)} 再過 ${addMin} 分鐘，是幾時幾分？`,
      options: shuffled,
      correctAnswer: correctIdx,
      explanation: `${startMin} + ${addMin} = ${startMin + addMin} 分；${Math.floor((startMin + addMin) / 60) > 0 ? `超過 60 分，小時 +1；` : ''}${startHour}:${pad2(startMin)} + ${addMin} 分 = ${correctStr}。`,
      kp: 'math.time.elapsed',
    };
  } else {
    // d3: time difference (how many minutes between two times)
    const startHour = randInt(rand, 8, 14);
    const startMin = randInt(rand, 0, 11) * 5;
    const elapsedMin = randInt(rand, 3, 11) * 5; // 15..55 in steps of 5

    const endTotalMin = startHour * 60 + startMin + elapsedMin;
    const endHour = Math.floor(endTotalMin / 60) % 24;
    const endMin = endTotalMin % 60;

    const correctStr = String(elapsedMin);
    const distractors = [
      String(elapsedMin + 5),
      String(elapsedMin - 5),
      String(elapsedMin + 10),
    ].filter(d => parseInt(d) > 0 && d !== correctStr);
    while (distractors.length < 3) distractors.push(String(elapsedMin + 15 + distractors.length * 5));

    const options = [correctStr, ...distractors.slice(0, 3)];
    const shuffled = [...options].sort((x, y) => mulberry32(seed + parseInt(x))() - mulberry32(seed + parseInt(y))());
    const correctIdx = shuffled.indexOf(correctStr);

    return {
      kind: 'mcq',
      prompt: `從 ${startHour}:${pad2(startMin)} 到 ${endHour}:${pad2(endMin)}，經過幾分鐘？`,
      options: shuffled,
      correctAnswer: correctIdx,
      explanation: `${endHour}:${pad2(endMin)} - ${startHour}:${pad2(startMin)} = ${elapsedMin} 分鐘。`,
      kp: 'math.time.elapsed',
    };
  }
}
